// © 2015-2021 WDNA - All Rights Reserved

import _ from 'lodash';
import * as React from 'react';

import Select2, { DataSelect2 } from 'wdna-select2';
import { RowDataUpdatedEvent, ICellRendererParams } from 'wdna-grid-table/lib/types';
import Table, { TableButtonsConfig } from 'wdna-components/lib/table/Table';
import ContainerModal from 'wdna-components/lib/modals/generics/ContainerModal';
import SelectorModal from 'wdna-components/lib/modals/selectors/CustomSelectorModal';
import { TabConfig, addTab, removeTab } from 'wdna-components/lib/tabs';
import { messageError, messageInfo, messageWarning, messageBoxText, messageBoxInfo, } from 'wdna-components/lib/helpers/notification';
import { getErrorMessage } from 'wdna-components/lib/helpers/response';
import { hasLevel } from 'wdna-components/lib/helpers/permission-new';

import { LovDO } from '../../../../types';
import { authorization } from '../../../common/requests';
import { SensorDO } from '../../../sensors/requests';
import { client, ClientDO } from '../../requests';

import Edit from './Edit';

type Props = unknown;

interface State {
  rowId?: number;
  userId?: number;
  update?: any;
  loading: boolean;
  data?: ClientDO[];
  userList: LovDO[];
  valueListA?: SensorDO[];
  valueListS?: string[];
  showGrid?: boolean;
  showSelectorUser?: boolean;
  showSelectorSensor?: boolean;
}

export default class List extends React.Component <Props, State> {
  private pid: string;
  private buttons: TableButtonsConfig;
  private mounted: boolean;

  public constructor(props: Props) {
    super(props);

    this.pid = window.getStr();
    this.mounted = false;

    this.buttons = {
      add: hasLevel('role', ['ADM', 'URW']),
      del: hasLevel('role', ['ADM', 'URW']),
      edit: true,
      assign: hasLevel('role', ['ADM', 'URW']),
      labels: {
        assign: 'Sensors',
      }
    };

    this.state = {
      update: window.getStr(),
      loading: false,
      data: [],

      userList: [],
      valueListA: [],
      valueListS: [],

      showGrid: false,
      showSelectorUser: false,
      showSelectorSensor: false,
    };
  }

  public componentDidMount(): void {
    this.mounted = true;
    this.load().catch(() => { /* pass */ });
  }

  public shouldComponentUpdate(nextProps: Props, nextState: State): boolean {
    return this.state.update !== nextState.update;
  }

  public componentWillUnmount(): void {
    this.mounted = false;
  }

  public setLocalState = (nextState: Partial<State>, callback?: (() => void)): void => {
    if (this.mounted) {
      this.setState((prevState: State) => ({
        ...prevState,
        ...nextState,
      }), callback);
    }
  };

  public load = async (): Promise<void> => {
    this.loadingStart();
    try {
      const customerList: ClientDO[] = await client<ClientDO[]>('list') || [];
      const customerEmails: Set<string> = new Set(customerList.map((item: ClientDO) => item.email));
      const userListIni: LovDO[] = await authorization<LovDO[]>('users', { application: window.APP_CODE, role: 'client' }) || [];
      const userListEnd: LovDO[] = userListIni.filter((item: LovDO) => !customerEmails.has(/.*\[(.*)\]/.exec(item?.name || '')?.[1] || ''));
      this.setLocalState({
        data: customerList,
        userList: userListEnd,
      });
    } catch (error) {
      messageError('Error', getErrorMessage(error));
    }
    this.loadingFinish();
  };

  public loadingStart = (): void => {
    this.setLocalState({
      update: window.getStr(),
      loading: true,
    });
  };

  public loadingFinish = (): void => {
    this.setLocalState({
      update: window.getStr(),
      loading: false,
    });
  };

  public onAdd = (): void => {
    this.setLocalState({
      update: window.getStr(),
      showSelectorUser: true,
    });
  };

  public onAssign = (value: ClientDO): void => {
    const rowId: number = value.id;
    this.setLocalState({ rowId });
    setTimeout(() => {
      this.loadValues(rowId).catch(() => { /* pass */ });
    }, 0);
  };

  public loadValues = async (rowId: number): Promise<void> => {
    try {
      const valueListOK: SensorDO[] = await client<SensorDO[]>('sensors_get', String(rowId), { assigned: 'true' }) || [];
      const valueListKO: SensorDO[] = await client<SensorDO[]>('sensors_get', String(rowId), { assigned: 'false' }) || [];

      const valueListS: string[] = [];
      valueListOK.forEach((sensorAs: SensorDO) => {
        valueListS.push(String(sensorAs.id));
      });

      this.setLocalState({
        update: window.getStr(),
        valueListA: valueListKO.concat(valueListOK),
        valueListS,
      });
      this.updModal('showSelectorSensor', true);
    } catch (error) {
      messageError('Error', getErrorMessage(error));
    }
  };

  public updModal = (modalName: string = 'none', sh: boolean = false): void => {
    this.setLocalState({
      update: window.getStr(),
      [modalName]: sh,
    });
  };

  public loadTab = (rowId?: number, email?: string): void => {
    const tabId: string = 'CuE';
    const title: string = rowId ? `Customer Edit: ${rowId}` : 'Customer New';

    window.store.dispatch(removeTab(tabId));
    const tabData: TabConfig = {
      id: tabId,
      active: true,
      title,
      component: Edit,
      props: {
        rowId,
        email,
        tabId: this.pid,
        report: 'CUSTOMERS',
        title: 'Customers',
        notify: this.notify,
      },
    };
    window.store.dispatch(addTab(tabData));
  };

  public notify = (): void => {
    this.load().catch(() => { /* pass */ });
  };

  public onSave = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.preventDefault();
    const { userList, userId } = this.state;
    if (userId) {
      this.updModal('showSelectorUser', false);
      const user: LovDO | undefined = userList.find((item: LovDO) => item.id === +userId);
      const email: string | undefined = /.*\[(.*)\]/.exec(user?.name || '')?.[1];
      if (!_.isEmpty(email)) {
        window.store.dispatch(removeTab('CuE'));
        setTimeout(() => {
          this.loadTab(undefined, email);
        }, 0);
      } else {
        messageError('Error', 'Missing email info on selected user');
      }
    } else {
      messageInfo('Info', 'You must select an existing user to create');
    }
  };

  public onEdit = (item: ClientDO): void => {
    window.store.dispatch(removeTab('CuE'));
    const rowId: number = item.id;
    setTimeout(() => {
      this.loadTab(rowId);
    }, 0);
  };

  public onDelete = (item: ClientDO): void => {
    const confirm: string = String(item.id);
    messageBoxText('Delete', `Enter '${confirm}' to confirm you want to delete the item: ${item.email}?`, (value?: string) => {
      if (confirm === value) {
        this.loadingStart();
        client('del', String(item.id))
          ?.then(() => {
            messageInfo('Success', 'Register successfully deleted', 5000);
            this.load().catch(() => { /* pass */ });
          })
          .catch((error: any) => {
            messageError('Error', getErrorMessage(error));
          })
          .finally(() => {
            this.loadingFinish();
          });
      } else {
        messageWarning('Warning', 'Wrong input parameter', 5000);
      }
    }, 'remove-circle', undefined, confirm);
  };

  public rowDataUpdated = (_event: RowDataUpdatedEvent): void => {
    this.setLocalState({
      update: window.getStr(),
      showGrid: true,
    });
  };

  public handleChangeValues = (counters: any[]): void => {
    const { rowId } = this.state;

    if (rowId) {
      this.updModal('showSelectorSensor', false);

      const valueListS: any[] = [];
      counters.forEach((counter: any) => {
        valueListS.push(counter);
      });

      this.setLocalState({
        valueListS,
      }, () => { this.handleSubmit(rowId); });
    }
  };

  public handleSubmit = (rowId: number): void => {
    const { valueListS } = this.state;

    messageBoxInfo('Edit', 'Do you want to accept this assignment?', () => {
      this.loadingStart();
      client('sensors_set', String(rowId), { sensors: valueListS })
        ?.then(() => {
          messageInfo('Success', 'Sensors successfully assigned', 5000);
          this.load().catch(() => { /* pass */ });
        })
        .catch((error: any) => {
          messageError('Error', getErrorMessage(error));
        })
        .finally(() => {
          this.loadingFinish();
        });
    });
  };

  public handleSelect = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
    const { value, name } = e.target;
    this.setLocalState({ [name]: value });
  };

  // TODO: generic ??
  public listCellRenderer = (params: ICellRendererParams): React.ReactNode => {
    const { value } = params;
    if (_.isArray(value)) {
      const style: React.CSSProperties = {
        backgroundColor: '#064754', borderRadius: '4px', padding: '3px 5px', margin: '2px',
      };
      const result: JSX.Element[] = value.map((item: string): JSX.Element => {
        return <span style={style}>{item}</span>;
      });

      return _.isEmpty(result) ? '' : <div>{result}</div>;
    }

    return value || '';
  };

  public apiKeyCellRenderer = (params: ICellRendererParams): React.ReactNode => {
    const { value } = params;
    const color: string = _.isEmpty(value) ? 'gray' : 'green';

    return <i className="fa fa-circle" style={{ color }} />;
  };

  public render(): JSX.Element {
    const { loading, data, valueListA, valueListS, userList, userId, showSelectorSensor, showSelectorUser, showGrid } = this.state;

    const columnAlias: Record<string, string> = {
      selectedRow: ' ',
      id: 'ID',
      email: 'Email',
      active: 'Active',
      companyName: 'Company',
      apikey: 'API',
      accessList: 'Dashboards',
    };
    const columnsToHide: string[] = [
      'companyId',
      'numSensors',
    ];
    const columnStyles: Record<string, any> = {
      id: { textAlign: 'right' },
      email: { textAlign: 'left' },
      active: { textAlign: 'center' },
      companyName: { textAlign: 'left' },
      accessList: { textAlign: 'left' },
      apikey: { textAlign: 'center' },
      actionButtons: { textAlign: 'center' },
    };
    const customColumnsDefs: Record<string, any> = {
      id: { minWidth: 60, maxWidth: 60 },
      email: { minWidth: 250, maxWidth: 250 },
      active: { minWidth: 100, maxWidth: 100 },
      companyName: { minWidth: 200, maxWidth: 200 },
      apikey: { minWidth: 100, maxWidth: 100, cellRenderer: this.apiKeyCellRenderer },
      accessList: { cellRenderer: this.listCellRenderer },
    };
    const selectorAlias: Record<string, string> = {
      code: 'Code',
      name: 'Name',
      status: 'Status',
      companyName: 'Company',
      locationName: 'Location',
      groupsList: 'Groups'
    };
    const selectorStyles: Record<string, any> = {
      id: { textAlign: 'center' },
      name: { textAlign: 'left' },
      status: { textAlign: 'left' },
      companyName: { textAlign: 'left' },
      locationName: { textAlign: 'left' },
      groupsList: { textAlign: 'left' },
    };
    const selectorHide: string[] = [
      'code',
      'lastTmsp',
      'checkTmsp',
      'command1',
      'command2',
      'command3',
      'update',
      'persistent1',
      'persistent2',
      'companyId',
      'firmwareId',
      'firmwareVer',
      'firmwareFile',
      'typeSensorId',
      'typeSensorName',
      'locationId',
      'latitude',
      'longitude',
      'nocxTime',
      'alarm',
      'adjusted',
      'dateInstall',
      'dateOnline',
      'dateOffline',
      'dateAction',
      'sim',
      'router',
      'comments',
      'companyType',
      'companyContact',
      'companyPhone',
      'companyEmail',
      'lastDate',
      'lastAdrs',
      'checkNocx',
      'update',
      'open',
      'shared',
      'alarm',
      'description',
      'alerts',
      'lastGateway'
    ];
    const customSelectorDefs: Record<string, any> = {
      id: { minWidth: 100, maxWidth: 100 },
      name: { minWidth: 135, maxWidth: 135 },
      status: { minWidth: 100, maxWidth: 100 },
      companyName: { minWidth: 125, maxWidth: 125 },
      groupsList: { cellRenderer: this.listCellRenderer },
    };

    return (
      <div id="content" className="content" style={{ visibility: (showGrid ? 'visible' : 'hidden') }}>
        <Table
          data={data || []}
          alias={columnAlias}
          hiddenCols={columnsToHide}
          customCellStyles={columnStyles}
          customColumnsDefs={customColumnsDefs}
          buttons={this.buttons}
          onEdit={this.onEdit}
          onAdd={this.onAdd}
          onDelete={this.onDelete}
          onAssign={this.onAssign}
          loading={loading}
          rowDataUpdated={this.rowDataUpdated}
        />
        { /* User selector */ }
        <ContainerModal
          title="Select the client's user"
          fullScreenButton={false}
          show={showSelectorUser}
          // eslint-disable-next-line react/jsx-no-bind
          onClose={this.updModal.bind(this, 'showSelectorUser', false)}
          modalClass="selector-modal"
          footerButtons={[
            {
              text: 'Save',
              className: 'btn btn-primary',
              onClick: this.onSave,
            },
          ]}
        >
          <Select2
            name="userId"
            value={userId}
            onChange={this.handleSelect}
            data={userList.map((item: LovDO): DataSelect2 => {
              return { id: item.id, text: item.name };
            })}
            options={{ placeholder: 'Select user' }}
            style={{ width: '80%' }}
          />
        </ContainerModal>
        { /* Sensor selector */ }
        <SelectorModal
          title="Assign Sensors to customer"
          alias={selectorAlias}
          items={valueListA || []}
          selectedItems={valueListS}
          hiddenCols={selectorHide}
          customCellStyles={selectorStyles}
          customColumnsDefs={customSelectorDefs}
          show={showSelectorSensor}
          maxElements={10000}
          // eslint-disable-next-line react/jsx-no-bind
          onCancel={this.updModal.bind(this, 'showSelectorSensor', false)}
          onSave={this.handleChangeValues}
        />
      </div>
    );
  }
}
